import { useEffect, useMemo, useState } from 'react';
import { notificationService, Notification as AppNotification } from '../services/notificationService';
import { supabase } from '../services/supabaseService';
import authService from '../services/authService';
import { logger } from '../utils/logger';

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    initializeNotifications();

    const currentUser = authService.getCurrentSession()?.user;
    if (!currentUser) return;

    const onInsert = (payload: any) => {
      loadNotifications();
      if ('Notification' in window && Notification.permission === 'granted') {
        const n = payload.new as any;
        new Notification(n.title || 'Nueva notificación', {
          body: n.body || '',
          icon: '/logo-ceroideas.png',
          tag: n.id,
        });
      }
    };

    // Dos suscripciones porque los `postgres_changes` filter no aceptan OR:
    // (1) notifs dirigidas a este usuario  (2) broadcasts (user_id IS NULL).
    const targetedChannel = supabase
      .channel('notifications-targeted')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${currentUser.id}`,
      }, onInsert)
      .subscribe();

    const broadcastChannel = supabase
      .channel('notifications-broadcast')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: 'user_id=is.null',
      }, onInsert)
      .subscribe();

    // Fallback polling: si realtime se cae, garantizamos frescura razonable.
    const intervalId = setInterval(loadNotifications, 60000);

    return () => {
      clearInterval(intervalId);
      supabase.removeChannel(targetedChannel);
      supabase.removeChannel(broadcastChannel);
    };
  }, []);

  const initializeNotifications = async () => {
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
      await loadNotifications();
      setIsInitialized(true);
    } catch (error) {
      logger.error('Error inicializando notificaciones', error);
      setIsInitialized(true);
    }
  };

  // Cargamos también las leídas (últimas 50) para que el usuario pueda ver
  // el historial reciente y desmarcar. El `markAsRead` ya no elimina de la
  // lista: sólo marca la fila con read_at.
  const loadNotifications = async () => {
    try {
      setLoading(true);
      const rows = await notificationService.getUserNotifications(50, true);
      setNotifications(rows);
    } catch (error) {
      logger.error('Error loading notifications', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const unreadCount = useMemo(
    () => notifications.filter((n) => n.read_at === null).length,
    [notifications]
  );

  const markAsRead = async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read_at: new Date() } : n))
      );
    } catch (error) {
      logger.error('Error marking as read', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      const now = new Date();
      setNotifications((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: now })));
    } catch (error) {
      logger.error('Error marking all as read', error);
    }
  };

  const notifyNewLead = async (leadName: string, assignedUserId?: string) => {
    if (!isInitialized) return;
    try {
      await notificationService.notifyNewLead(leadName, assignedUserId);
    } catch (error) {
      logger.error('Error notifying new lead', error);
    }
  };

  const notifyMeetingScheduled = async (meetingTitle: string, contactName: string, assignedUserId: string) => {
    if (!isInitialized) return;
    try {
      await notificationService.notifyMeetingScheduled(meetingTitle, contactName, assignedUserId);
    } catch (error) {
      logger.error('Error notifying meeting scheduled', error);
    }
  };

  const notifyContactCreated = async (contactName: string, company: string, assignedUserId?: string) => {
    if (!isInitialized) return;
    try {
      await notificationService.notifyContactCreated(contactName, company, assignedUserId);
    } catch (error) {
      logger.error('Error notifying contact created', error);
    }
  };

  const notifyCompanyCreated = async (companyName: string, assignedUserId?: string) => {
    if (!isInitialized) return;
    try {
      await notificationService.notifyCompanyCreated(companyName, assignedUserId);
    } catch (error) {
      logger.error('Error notifying company created', error);
    }
  };

  const notifyLeadConverted = async (leadName: string, company: string, assignedUserId?: string) => {
    if (!isInitialized) return;
    try {
      await notificationService.notifyLeadConverted(leadName, company, assignedUserId);
    } catch (error) {
      logger.error('Error notifying lead converted', error);
    }
  };

  const notifyUserCreated = async (userName: string, userEmail: string, assignedUserId?: string) => {
    if (!isInitialized) return;
    try {
      await notificationService.notifyUserCreated(userName, userEmail, assignedUserId);
    } catch (error) {
      logger.error('Error notifying user created', error);
    }
  };

  const notifyActivityCreated = async (activityType: string, contactName: string, assignedUserId?: string) => {
    if (!isInitialized) return;
    try {
      await notificationService.notifyActivityCreated(activityType, contactName, assignedUserId);
    } catch (error) {
      logger.error('Error notifying activity created', error);
    }
  };

  const notifyQontoIncome = async (amount: number, counterparty: string, needsAssignment: boolean = false) => {
    if (!isInitialized) return;
    try {
      await notificationService.notifyQontoIncome(amount, counterparty, needsAssignment);
    } catch (error) {
      logger.error('Error notifying qonto income', error);
    }
  };

  return {
    notifications,
    unreadCount,
    isInitialized,
    loading,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    notifyNewLead,
    notifyMeetingScheduled,
    notifyContactCreated,
    notifyCompanyCreated,
    notifyLeadConverted,
    notifyUserCreated,
    notifyActivityCreated,
    notifyQontoIncome,
  };
};
